import React, {useState, useEffect, ReactNode} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {MONOMER_QUERY, NucleotideComponent} from 'molbio/monomers/MonomerConstants';
import {SelfOpeningMonomerDropdown} from 'molbio/monomers/MonomerDropdown';
import {getMonomerIdFromMappingOrNaturalBase} from 'molbio/monomers/MonomersUtil';

export interface MonomerMapping {
  [MonomerNaturalAnalogue.A]: string;
  [MonomerNaturalAnalogue.C]: string;
  [MonomerNaturalAnalogue.G]: string;
  [MonomerNaturalAnalogue.U]: string;
}

interface MonomerMappingTableProps {
  adenineMonomerId: string;
  cytosineMonomerId: string;
  guanineMonomerId: string;
  uracilMonomerId: string;
  onSetMonomerMapping: (monomerMapping: MonomerMapping) => void;
  sectionHeader?: ReactNode;
  disabled?: boolean;
}

function MonomerMappingTable({
  adenineMonomerId,
  cytosineMonomerId,
  guanineMonomerId,
  uracilMonomerId,
  onSetMonomerMapping,
  sectionHeader,
  disabled,
}: MonomerMappingTableProps): JSX.Element {
  const [monomerState, setMonomerState] = useState({
    [MonomerNaturalAnalogue.A]: adenineMonomerId,
    [MonomerNaturalAnalogue.C]: cytosineMonomerId,
    [MonomerNaturalAnalogue.G]: guanineMonomerId,
    [MonomerNaturalAnalogue.U]: uracilMonomerId,
  });

  useEffect(() => {
    onSetMonomerMapping(monomerState);
  }, [monomerState, onSetMonomerMapping]);

  return (
    <>
      {sectionHeader}
      <table className="table">
        <thead>
          <tr>
            <td>
              <strong>Nucleotide Base</strong>
            </td>
            <td>
              <strong>Interpret as</strong>
            </td>
          </tr>
        </thead>
        <tbody>
          {[
            ['A / a', MonomerNaturalAnalogue.A],
            ['C / c', MonomerNaturalAnalogue.C],
            ['G / g', MonomerNaturalAnalogue.G],
            ['U / u', MonomerNaturalAnalogue.U],
          ].map(([label, naturalAnalogue]: [string, 'A' | 'C' | 'G' | 'U']) => (
            <tr key={label}>
              <td style={{width: '20%'}}>{label}</td>
              <td>
                <div style={{width: '250px'}}>
                  <SelfOpeningMonomerDropdown
                    fullWidth={true}
                    nameMaxLength={30}
                    nucleotideComponent={NucleotideComponent.BASE}
                    filterMonomersPredicate={(mon) => {
                      if (
                        BenchlingConfig.MOLBIO_ENABLE_UT_FLEXIBILITY &&
                        naturalAnalogue === MonomerNaturalAnalogue.U
                      ) {
                        return [MonomerNaturalAnalogue.U, MonomerNaturalAnalogue.T].includes(
                          mon.naturalAnalogue
                        );
                      } else {
                        return mon.naturalAnalogue === naturalAnalogue;
                      }
                    }}
                    selectedMonomerId={monomerState[naturalAnalogue]}
                    updateSelectedMonomerId={(newId) => {
                      setMonomerState({...monomerState, [naturalAnalogue]: newId});
                    }}
                    disabled={disabled}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export default function MonomerMappingForm({
  onSetMonomerMapping,
  monomerMapping,
  disabled,
  sectionHeader,
}: {
  onSetMonomerMapping: (monomerMapping: MonomerMapping) => void;
  monomerMapping?: MonomerMapping | null;
  disabled?: boolean;
  sectionHeader?: ReactNode;
}): JSX.Element {
  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}>
      {(monomerData) => (
        <MonomerMappingTable
          adenineMonomerId={getMonomerIdFromMappingOrNaturalBase(
            MonomerNaturalAnalogue.A,
            monomerData.monomers,
            monomerMapping
          )}
          cytosineMonomerId={getMonomerIdFromMappingOrNaturalBase(
            MonomerNaturalAnalogue.C,
            monomerData.monomers,
            monomerMapping
          )}
          guanineMonomerId={getMonomerIdFromMappingOrNaturalBase(
            MonomerNaturalAnalogue.G,
            monomerData.monomers,
            monomerMapping
          )}
          uracilMonomerId={getMonomerIdFromMappingOrNaturalBase(
            MonomerNaturalAnalogue.U,
            monomerData.monomers,
            monomerMapping
          )}
          onSetMonomerMapping={onSetMonomerMapping}
          disabled={disabled}
          sectionHeader={sectionHeader}
        />
      )}
    </BenchlingQuery>
  );
}
